import type {
  DatumHandle,
  LevelHandle,
  RoomHandle,
  StairBlockHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { RWTable, Table } from "common/data/table";
import type { Plane } from "geom/planeTypes";
import type { VerticalJustification, HorizontalJustification } from "./datumsTypesEnum";
import {
  WallLateralDatumAnchor,
  WallHorizontalDatum,
  FoundationDatumVerticalAnchor,
  WallVerticalDatumAnchor,
} from "./datumsTypesEnum";

export const GRADE_DATUM_ID = "qQWYrvGzkjRIAaIx";

type DatumTypes = "VERTICAL" | "HORIZONTAL" | "LATERAL";

export const DatumType: { [K in DatumTypes]: K } = {
  VERTICAL: "VERTICAL",
  LATERAL: "LATERAL",
  HORIZONTAL: "HORIZONTAL",
} as const;

export interface DatumBaseType {
  readonly catalogItemType: "DATUM"; // Since datum is no longer a catalog item, we need to add this field to the type.
  readonly datumType: DatumTypes;
  readonly offset?: number;
  readonly anchor?: any;
  readonly name?: string;
  readonly offsetNormalized?: boolean;
  readonly justification?: any;
  readonly editable?: boolean;
}

export type DatumBase<T> = {
  readonly anchor?: T;
} & DatumBaseType;

/**
 * building system datums are datums that have meaning within
 * specific building systems
 */
export type BuildingSystemDatum = VerticalDatumType | HorizontalDatumType | LateralDatumType;

export type BuildingSystemDatumTable = Table<BuildingSystemDatum>;

/**
 * T needs to be replaced with LevelDatumAnchor once FoundationDatumAnchor === LevelDatumAnchor
 */
export interface VerticalDatum<T> extends DatumBase<T> {
  readonly datumType: "VERTICAL";
}

export interface LateralDatum<T> extends DatumBase<T> {
  readonly datumType: "LATERAL";
}

export interface HorizontalDatum<T> extends DatumBase<T> {
  readonly datumType: "HORIZONTAL";
}

/**
 * TODO create discriminator function to determine if a datum is builtin or custom
 * at runtime for rekeying purposes
 */
export type VerticalDatumType = VerticalDatumTypeBuiltin | VerticalDatumTypeCustom;
export type HorizontalDatumType = HorizontalDatumTypeBuiltin | HorizontalDatumTypeCustom;
export type LateralDatumType = LateralDatumTypeBuiltin | LateralDatumTypeCustom;

export interface VerticalDatumTypeBuiltin extends DatumBaseType {
  readonly editable: false;
  readonly datumType: "VERTICAL";
  readonly justification?: VerticalJustification;
}

export interface HorizontalDatumTypeBuiltin extends DatumBaseType {
  readonly editable: false;
  readonly datumType: "HORIZONTAL";
  readonly justification?: HorizontalJustification;
}

export interface LateralDatumTypeBuiltin extends DatumBaseType {
  readonly editable: false;
  readonly datumType: "LATERAL";
}

export interface VerticalDatumTypeCustom extends DatumBaseType {
  readonly editable: true;
  readonly datumType: "VERTICAL";
  readonly justification?: VerticalJustification;
}

export interface HorizontalDatumTypeCustom extends DatumBaseType {
  readonly editable: true;
  readonly datumType: "HORIZONTAL";
  readonly justification?: HorizontalJustification;
}

export interface LateralDatumTypeCustom extends DatumBaseType {
  readonly editable: true;
  readonly datumType: "LATERAL";
}

/**
 * A larger collection of lateral datums to maintain the existing list that many entities use.
 */
export const WallLateralDatumAnchorAll = {
  ...WallLateralDatumAnchor,
  OUTSIDE_FACE_OF_FINISH: "OUTSIDE_FACE_OF_FINISH",
  INSIDE_FACE_OF_FRAMING: "INSIDE_FACE_OF_FRAMING",
  INSIDE_FACE_OF_SUBSTRATE: "INSIDE_FACE_OF_SUBSTRATE",
  INSIDE_FACE_OF_FINISH: "INSIDE_FACE_OF_FINISH",
  ROOM_EDGE: "ROOM_EDGE",
  WALL_OFFSET: "WALL_OFFSET",
} as const;
export type WallLateralDatumAnchorAll = (typeof WallLateralDatumAnchorAll)[keyof typeof WallLateralDatumAnchorAll];

export const isValidVoidWallLateralDatum: { [K in WallLateralDatumAnchorAll]?: boolean } = {
  [WallLateralDatumAnchorAll.ROOM_EDGE]: true,
};

/**
 * Utility for determining whether a WallLateralDatumAnchor is an inner offset or outer offset from the room edge.
 */
export const lateralDatumInwardWallLayers: { [K in WallLateralDatumAnchorAll]?: boolean } = {
  [WallLateralDatumAnchorAll.INSIDE_FACE_OF_FRAMING]: true,
  [WallLateralDatumAnchorAll.INSIDE_FACE_OF_SUBSTRATE]: true,
  [WallLateralDatumAnchorAll.INSIDE_FACE_OF_FINISH]: true,
};

//using import to alias as enums
export import ColumnHorizontalDatum = WallHorizontalDatum;

export const isValidVoidWallHorizontalDatum: { [K in WallHorizontalDatum]?: boolean } = {
  [WallHorizontalDatum.ROOM_EDGE]: true,
};

export interface FacadeVerticalDatum extends VerticalDatum<WallVerticalDatumAnchor> {
  readonly name?: string;
  readonly offsetNormalized?: boolean;
  readonly justification?: VerticalJustification;
}
export interface FacadeHorizontalDatum extends HorizontalDatum<WallHorizontalDatum> {
  readonly name?: string;
  readonly offsetNormalized?: boolean;
  readonly justification?: HorizontalJustification;
}

export type FacadeDatum = FacadeHorizontalDatum | FacadeVerticalDatum;
export type FacadeDatumTable = Table<FacadeDatum>;

export interface FacadeVerticalDatumCatalog extends VerticalDatum<WallVerticalDatumAnchor> {
  readonly justification: VerticalJustification;
}

export interface FacadeHorizontalDatumCatalog extends VerticalDatum<WallHorizontalDatum> {
  readonly justification: HorizontalJustification;
}

export const FacadeVerticalDatumAnchor = {
  ...WallVerticalDatumAnchor,
};

export const FacadeHorizontalDatumAnchor = {
  ...WallHorizontalDatum,
};

export const FoundationLateralDatumAnchor = {
  ...WallLateralDatumAnchor,
};

export const VerticalDatumAnchor = {
  ...FacadeVerticalDatumAnchor,
  ...FoundationDatumVerticalAnchor,
};

export const HorizontalDatumAnchor = {
  ...FacadeHorizontalDatumAnchor,
};

export const LateralDatumAnchor = {
  ...FoundationLateralDatumAnchor,
};

export interface WallAssemblyHorizontalDatum {
  readonly datumType: "HORIZONTAL";
  readonly justification: HorizontalJustification;
  readonly offset: number;
  readonly offsetNormalized: boolean;
}
export interface WallAssemblyVerticalDatum {
  readonly datumType: "VERTICAL";
  readonly anchor: WallVerticalDatumAnchor;
  readonly justification: VerticalJustification;
  readonly offset: number;
  readonly offsetNormalized: boolean;
}
export type ResolvedDatumTable = RWTable<ResolvedBuildingDatum>;
export interface ResolvedBuildingDatum {
  id: string;
  name: string;
  source: ResolvedBuildingDatumHandle | null;
  plane: Plane;
}
export type ResolvedBuildingDatumHandle = DatumHandle | LevelHandle | RoomHandle | StairBlockHandle;
export type ResolvedBuildingDatumType = ResolvedBuildingDatumHandle["type"];
